// API for the Digital Signage dashboard — branches, Pi devices, the media
// library, and playlists. Access: Admin / Executive Team manage everything;
// a General Manager manages the signage branch tied to their profile branch.
const jsonRequest = async (url, options = {}) => {
  const res = await fetch(url, options);
  // DELETEs answer 204 with no body.
  const content = res.status === 204 ? null : await res.json();
  if (!res.ok) throw new Error(content?.error || "Request failed");
  return content;
};

// The signage endpoints identify the caller by this header (no token
// validation server-side — the same trust model the PCW admin routes use),
// so every call needs the signed-in user's id.
const request = (url, userId, { body, ...options } = {}) =>
  jsonRequest(url, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      "x-user-id": userId || "",
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

// Pass as a branchId to the asset/playlist calls below for the company-wide
// library and playlists (any location's displays can play them; only Admin/
// Executive Team can change them).
export const SIGNAGE_COMPANY_SCOPE = "company";

// ---- Branches ----

export const fetchSignageBranches = (userId) =>
  request("/api/signage/branches", userId);

// branch: { name, userBranch?, locationLabel? } — userBranch is the
// user_data.branch value whose General Manager may manage this branch.
export const createSignageBranch = (branch, userId) =>
  request("/api/signage/branches", userId, { method: "POST", body: branch });

export const updateSignageBranch = (id, updates, userId) =>
  request(`/api/signage/branches/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: updates,
  });

export const deleteSignageBranch = (id, userId) =>
  request(`/api/signage/branches/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// ---- Devices ----

// Every display the caller can see, across branches (fleet view).
export const fetchAllSignageDevices = (userId) =>
  request("/api/signage/devices", userId);

export const fetchSignageDevices = (branchId, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/devices`,
    userId
  );

// Register the PIN a Pi shows on its screen at first boot: { pin, name }.
export const registerSignageDevice = (branchId, device, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/devices`,
    userId,
    { method: "POST", body: device }
  );

export const renameSignageDevice = (id, name, userId) =>
  request(`/api/signage/devices/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: { name },
  });

// mode: "playlist" (media loop) | "board" (browser kiosk on the live shop
// clock page; only branches with the shop board option — PCW / Mill). The Pi switches within ~1 min.
export const setSignageDeviceMode = (id, mode, userId) =>
  request(`/api/signage/devices/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: { mode },
  });

// Live shop board screen timing: { clockSeconds?, jobsSeconds?, weekSeconds?,
// deadlinesSeconds? } — seconds each screen shows (0 = off, otherwise
// 5–3600; at least one stays on). The board picks it up within a few seconds.
export const setSignageDeviceBoard = (id, board, userId) =>
  request(`/api/signage/devices/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: { board },
  });

// playlistIds: the device's playlists in play order (its branch's and/or
// company ones) — the Pi plays each one's items in turn. [] clears.
export const setSignageDevicePlaylists = (deviceId, playlistIds, userId) =>
  request(
    `/api/signage/devices/${encodeURIComponent(deviceId)}/playlists`,
    userId,
    { method: "PUT", body: { playlistIds } }
  );

export const deleteSignageDevice = (id, userId) =>
  request(`/api/signage/devices/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// Queue a remote command the Pi picks up on its next check-in (~30s):
// "reboot" | "restart" | "refresh" | "identify".
export const sendSignageCommand = (deviceId, type, userId) =>
  request(`/api/signage/devices/${encodeURIComponent(deviceId)}/command`, userId, {
    method: "POST",
    body: { type },
  });

// ---- Settings (org-wide; Admin/Executive Team only) ----

// refresh: re-check the YouTube import tools now (not the cached answer).
export const fetchSignageSettings = (userId, { refresh = false } = {}) =>
  request(`/api/signage/settings${refresh ? "?refresh=1" : ""}`, userId);

export const updateSignageSettings = (settings, userId) =>
  request("/api/signage/settings", userId, { method: "PUT", body: settings });

// Try an OpenWeather key before saving it → { ok, place, tempF, conditions }.
export const testSignageWeatherKey = (openWeatherApiKey, userId) =>
  request("/api/signage/settings/test-weather", userId, {
    method: "POST",
    body: { openWeatherApiKey },
  });

// ---- Assets (media library) ----

export const fetchSignageAssets = (branchId, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/assets`,
    userId
  );

// Multipart upload — no Content-Type header so the browser sets the boundary.
// extra (image editor "Save as copy"): { project, sourceFrom } — the layered
// project JSON and the asset whose original photo the copy was made from.
export const uploadSignageAsset = async (branchId, file, userId, extra = {}) => {
  const form = new FormData();
  form.append("file", file);
  if (extra.project) form.append("project", JSON.stringify(extra.project));
  if (extra.sourceFrom) form.append("sourceFrom", extra.sourceFrom);
  const res = await fetch(
    `/api/signage/branches/${encodeURIComponent(branchId)}/assets`,
    { method: "POST", headers: { "x-user-id": userId || "" }, body: form }
  );
  const content = await res.json();
  if (!res.ok) throw new Error(content?.error || "Upload failed");
  return content;
};

/**
 * Fetch an asset's bytes for previews and return an object URL (the auth
 * header can't ride along on an <img src>). Callers own revoking the URL.
 */
export const fetchSignageAssetBlobUrl = async (assetId, userId) => {
  const res = await fetch(
    `/api/signage/assets/${encodeURIComponent(assetId)}/raw`,
    { headers: { "x-user-id": userId || "" } }
  );
  if (!res.ok) throw new Error("Could not load preview");
  return URL.createObjectURL(await res.blob());
};

// ---- Image editor ----

// One asset plus its saved editor project (null if never edited).
export const fetchSignageAsset = (id, userId) =>
  request(`/api/signage/assets/${encodeURIComponent(id)}`, userId);

// The original photo an edited image was made from, as an object URL.
export const fetchSignageAssetSourceBlobUrl = async (assetId, userId) => {
  const res = await fetch(
    `/api/signage/assets/${encodeURIComponent(assetId)}/source`,
    { headers: { "x-user-id": userId || "" } }
  );
  if (!res.ok) throw new Error("Could not load the original image");
  return URL.createObjectURL(await res.blob());
};

// QR code PNG for the editor's QR layer: { data, dark, light, margin } —
// light "transparent" leaves the background see-through.
export const fetchSignageQrBlobUrl = async ({ data, dark, light, margin }, userId) => {
  const qs = new URLSearchParams({ data, dark, light, margin: String(margin ?? 2) });
  const res = await fetch(`/api/signage/qr?${qs}`, {
    headers: { "x-user-id": userId || "" },
  });
  if (!res.ok) {
    let message = "";
    try {
      message = (await res.json()).error;
    } catch {
      /* not JSON */
    }
    throw new Error(message || "Couldn't make the QR code");
  }
  return URL.createObjectURL(await res.blob());
};

// "Replace original": new rendered image (+ project) for the same asset, so
// every playlist using it updates.
export const replaceSignageAssetContent = async (id, file, project, userId) => {
  const form = new FormData();
  form.append("file", file);
  if (project) form.append("project", JSON.stringify(project));
  const res = await fetch(`/api/signage/assets/${encodeURIComponent(id)}/content`, {
    method: "PUT",
    headers: { "x-user-id": userId || "" },
    body: form,
  });
  const content = await res.json();
  if (!res.ok) throw new Error(content?.error || "Save failed");
  return content;
};

export const deleteSignageAsset = (id, userId) =>
  request(`/api/signage/assets/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// ---- YouTube import (clips of ≤3 min land in a library as MP4s) ----

// { videoId, title, channel, duration, thumbnail, maxClipSeconds } — also
// tells us early if the server can't import (yt-dlp/ffmpeg missing → error).
export const fetchYoutubeInfo = (url, userId) =>
  request(`/api/signage/youtube/info?url=${encodeURIComponent(url)}`, userId);

// A library's recent imports (running, failed, just finished).
export const fetchYoutubeImports = (branchId, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/youtube`,
    userId
  );

// clip: { url, start, end, filename? } in seconds — queued, runs in the
// background; poll fetchYoutubeImports for progress.
export const startYoutubeImport = (branchId, clip, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/youtube`,
    userId,
    { method: "POST", body: clip }
  );

// Cancel a running import, or dismiss a finished/failed one.
export const removeYoutubeImport = (id, userId) =>
  request(`/api/signage/youtube/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// 75 → "1:15", 3725 → "1:02:05".
export const formatClock = (sec) => {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

// ---- Playlists ----

// Every playlist the caller can see: company ones + their branches'.
export const fetchAllSignagePlaylists = (userId) =>
  request("/api/signage/playlists", userId);

export const fetchSignagePlaylists = (branchId, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/playlists`,
    userId
  );

export const createSignagePlaylist = (branchId, playlist, userId) =>
  request(
    `/api/signage/branches/${encodeURIComponent(branchId)}/playlists`,
    userId,
    { method: "POST", body: playlist }
  );

// updates: { name?, items?, startDate?, endDate?, weatherEnabled?, ... } —
// items replaces the full list; dates are "YYYY-MM-DD" or null to clear.
export const updateSignagePlaylist = (id, updates, userId) =>
  request(`/api/signage/playlists/${encodeURIComponent(id)}`, userId, {
    method: "PATCH",
    body: updates,
  });

export const deleteSignagePlaylist = (id, userId) =>
  request(`/api/signage/playlists/${encodeURIComponent(id)}`, userId, {
    method: "DELETE",
  });

// Today as "YYYY-MM-DD" on this computer's clock (playlist date ranges are
// calendar days, inclusive).
export const todayDateString = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// A playlist's date range right now: "running" (or no range), "upcoming",
// or "ended".
export const playlistScheduleState = (playlist, today = todayDateString()) => {
  if (playlist.endDate && playlist.endDate < today) return "ended";
  if (playlist.startDate && playlist.startDate > today) return "upcoming";
  return "running";
};

// "2026-11-01" → "Nov 1, 2026" (parsed as a local calendar day).
const formatDay = (value) => {
  const [y, m, d] = String(value).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// "Nov 1, 2026 – Dec 31, 2026" / "from Nov 1, 2026" / "until …" / "".
export const formatPlaylistRange = (playlist) => {
  const { startDate, endDate } = playlist;
  if (startDate && endDate) return `${formatDay(startDate)} – ${formatDay(endDate)}`;
  if (startDate) return `from ${formatDay(startDate)}`;
  if (endDate) return `until ${formatDay(endDate)}`;
  return "";
};

// "2621440" bytes → "2.5 MB"; keeps the media grid compact.
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// "just now" / "4 min ago" / "3 h ago" / "Sep 20" — device check-ins.
export const formatRelative = (value) => {
  if (!value) return "never";
  const ms = Date.now() - new Date(value).getTime();
  if (ms < 60 * 1000) return "just now";
  if (ms < 60 * 60 * 1000) return `${Math.floor(ms / 60000)} min ago`;
  if (ms < 24 * 60 * 60 * 1000) return `${Math.floor(ms / 3600000)} h ago`;
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

// 93784 seconds → "1d 2h"; keeps the health row compact.
export const formatUptime = (sec) => {
  const s = Math.max(0, Number(sec) || 0);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};
